import {
  getCaKeys,
  getCaSettingByKey,
  getCaSettings,
  getCaType,
} from "../../../../builders/settings/CaSetting";
import { calculateAllowance } from "../CaSchedules/utils/calculation";
import { getNetDisposalError } from "../CaSchedules/utils/s19dNetDisposal";
import { formatAmount } from "../../../../utils/hooks/dataHandler";

export const DEFAULT_CURRENT_YA = 2026;

export const EXTERNAL_CA_TYPES = [
  "Hire Purchase",
  "Renovation",
  "Revenue",
  "No Claim",
];
export const EXTERNAL_TYPES = new Set(EXTERNAL_CA_TYPES);

export const IA_RATES = {
  S19: 0.2,
  "S16 - LIA": 0.25,
  "S16 - IBA": 0.25,
};

export const ASSET_EXPENSED_OFF_CATEGORY = "Asset expensed off";
export const PL_SCHEDULES_CATEGORY = ASSET_EXPENSED_OFF_CATEGORY;

export const FIELD_LABELS = {
  assetCategory: "Asset Category",
  assetDescription: "Asset Description",
  caType: "CA Type",
  costOfAsset: "Cost of Asset",
  yaOfPurchase: "YA of Purchase",
  isPLScheduleAsset: "Asset expensed off",
  hp: "Hire Purchase",
  deferred: "Deferred in current year?",
  disposal: "Disposed in current year?",
  noOfYearsClaim: "No. of Years of Claim",
  noOfYearsBf: "No. of Years b/f",
  noOfYearsCf: "No. of Years c/f",
  twdvBf: "Base TWDV b/f",
  enhancedTwdvBf: "Enhanced TWDV b/f",
  iaRate: "IA Rate",
  ia: "IA",
  aa: "AA",
  twdvCf: "TWDV c/f",
};

const getSettings = (caSettings) =>
  Array.isArray(caSettings) && caSettings.length > 0
    ? caSettings
    : getCaSettings();

export const getFaRegisterCaTypes = (caSettings) => [
  ...getCaKeys(getSettings(caSettings)),
  ...EXTERNAL_CA_TYPES,
];

export const getFaRegisterCaTypeSelectOptions = (caSettings) => {
  const settings = getSettings(caSettings);

  return getFaRegisterCaTypes(settings).map((type) => ({
    value: type,
    label: type,
    group: EXTERNAL_TYPES.has(type)
      ? "Non-CA / External"
      : getCaType(settings, type),
  }));
};

export const getClaimYearsFromSetting = (caType, caSettings) => {
  const setting = getCaSettingByKey(getSettings(caSettings), caType);
  const years = setting?.numberOfYears;
  return years === null || years === undefined || years === ""
    ? null
    : Number(years);
};

export const isFixedClaimYears = (caType, caSettings) =>
  getClaimYearsFromSetting(caType, caSettings) !== null;

export const isEnhancedTwdvType = (caType, caSettings) =>
  Boolean(
    getCaSettingByKey(getSettings(caSettings), caType)?.enhancedAllowance
  );

export const hasInitialAllowance = (caType, caSettings) => {
  if (Object.hasOwn(IA_RATES, caType)) return true;
  const setting = getCaSettingByKey(getSettings(caSettings), caType);
  return Boolean(setting?.allowanceType?.IA || setting?.isFieldVariable?.IA);
};

const getInitialAllowanceRate = (caType, caSettings) => {
  const setting = getCaSettingByKey(getSettings(caSettings), caType);
  if (setting?.IA !== null && setting?.IA !== undefined && setting?.IA !== "") {
    return Number(setting.IA) / 100;
  }

  return IA_RATES[caType] ?? null;
};

export function createEmptyAsset(rowKey) {
  return {
    rowKey,
    assetCategoryId: "",
    assetCategory: "",
    assetDescription: "",
    costOfAsset: "",
    yaOfPurchase: "",
    caType: "",
    isPLScheduleAsset: false,
    hp: false,
    noOfYearsClaim: "",
    noOfYearsBf: "",
    deferred: false,
    disposal: false,
    noOfYearsCf: null,
    iaRate: null,
    twdvBf: "",
    enhancedTwdvBf: null,
    ia: null,
    aa: null,
    twdvCf: null,
    calculationWarning: "",
  };
}

export function isWholeNumber(value) {
  if (value === "" || value === null || value === undefined) return false;
  const number = Number(value);
  return Number.isInteger(number);
}

export function isApplicableType(caType) {
  return Boolean(caType) && !EXTERNAL_TYPES.has(caType);
}

const normalizeBoolean = (value) => {
  if (value === true || value === false) return value;
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  if (["true", "yes", "1"].includes(normalized)) return true;
  if (["false", "no", "0", ""].includes(normalized)) return false;
  return Boolean(value);
};

export function isAssetExpensedOffCategory(value) {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();

  return [ASSET_EXPENSED_OFF_CATEGORY].some(
    (category) => category.toLowerCase() === normalized
  );
}

export function isPLScheduleAsset(row = {}) {
  if (EXTERNAL_TYPES.has(row.caType)) return false;

  return (
    normalizeBoolean(row.isPLScheduleAsset) ||
    isAssetExpensedOffCategory(row.assetCategory)
  );
}

export function roundDollar(value) {
  return Math.floor(Number(value) + 0.5);
}

const toRegisterAllowanceAmount = (value) => {
  if (value === "Deferred" || value === null || value === undefined) return 0;
  return roundDollar(Math.abs(Number(value) || 0));
};

const calculateRegisterAllowance = (row, currentYa, caSettings) => {
  const caSetting = getCaSettingByKey(getSettings(caSettings), row.caType);
  if (!caSetting) return { ia: 0, aa: 0, totalAmount: Number(row.twdvBf) };

  const currentAsset = Number(row.yaOfPurchase) === Number(currentYa);
  const allowanceRow = calculateAllowance(
    {
      ...row,
      totalAmount: currentAsset
        ? Number(row.costOfAsset)
        : Number(row.twdvBf),
      ya: Number(row.yaOfPurchase),
      isDeferred: Boolean(row.deferred),
      caName: row.caType,
      cost: Number(row.costOfAsset),
      noOfYearClaims: Number(row.noOfYearsClaim),
      noOfYearClaimsBf: Number(row.noOfYearsBf),
      initialAllowanceRate:
        row.iaRate === null || row.iaRate === undefined
          ? null
          : Number(row.iaRate) * 100,
    },
    caSetting,
    Number(currentYa)
  );

  return {
    ia: toRegisterAllowanceAmount(allowanceRow.initialAllowance),
    aa: toRegisterAllowanceAmount(allowanceRow.annualAllowance),
    totalAmount: allowanceRow.totalAmount,
    s19dDisposalAdjustment: allowanceRow.s19dDisposalAdjustment,
    s19dWdaClaimedBf: allowanceRow.s19dWdaClaimedBf,
    calculationWarning: caSetting.caType === "S19D" ? getNetDisposalError(allowanceRow) : "",
  };
};

export function deriveAsset(
  input,
  currentYa = DEFAULT_CURRENT_YA,
  caSettings = getCaSettings()
) {
  const row = { ...input, calculationWarning: "" };
  // A CA type change must not carry S19D disposal history into another regime.
  if (getCaType(getSettings(caSettings), row.caType) !== "S19D") {
    for (const field of Object.keys(row)) {
      if (field.startsWith("s19d") || [
        "netDisposalConsideration", "netDisposalConsiderationEnabled", "assetInUseAtFYE",
      ].includes(field)) delete row[field];
    }
  }
  row.hp = row.caType === "Hire Purchase";
  const isExternalType = EXTERNAL_TYPES.has(row.caType);
  const isExpensedOffAsset =
    !row.hp &&
    !isExternalType &&
    (isAssetExpensedOffCategory(row.assetCategory) ||
      normalizeBoolean(row.isPLScheduleAsset));

  if (isExpensedOffAsset) {
    row.assetCategoryId = ASSET_EXPENSED_OFF_CATEGORY;
    row.assetCategory = ASSET_EXPENSED_OFF_CATEGORY;
  }

  row.isPLScheduleAsset = isExpensedOffAsset;

  const applicable = isApplicableType(row.caType);
  if (!applicable) {
    if (isExternalType && isAssetExpensedOffCategory(row.assetCategory)) {
      row.assetCategoryId = "";
      row.assetCategory = "";
    }
    row.isPLScheduleAsset = false;
    row.disposal = false;
  }
  const currentAsset = Number(row.yaOfPurchase) === Number(currentYa);
  const costValid =
    isWholeNumber(row.costOfAsset) && Number(row.costOfAsset) > 0;

  if (!applicable) {
    row.noOfYearsClaim = null;
    row.noOfYearsBf = null;
    row.deferred = null;
    row.noOfYearsCf = null;
    row.iaRate = null;
    row.twdvBf = null;
    row.enhancedTwdvBf = null;
    row.ia = null;
    row.aa = null;
    row.twdvCf = null;
    return row;
  }

  if (!isEnhancedTwdvType(row.caType, caSettings)) row.enhancedTwdvBf = null;

  const presetClaimYears = getClaimYearsFromSetting(row.caType, caSettings);
  if (presetClaimYears !== null) {
    row.noOfYearsClaim = presetClaimYears;
  }

  if (currentAsset) {
    row.noOfYearsBf = isWholeNumber(row.noOfYearsClaim)
      ? Number(row.noOfYearsClaim)
      : "";
    row.twdvBf = costValid ? Number(row.costOfAsset) : "";
  }

  if (row.deferred === null || row.deferred === undefined) row.deferred = false;

  const yearsBfValid =
    isWholeNumber(row.noOfYearsBf) && Number(row.noOfYearsBf) >= 0;
  const twdvBfValid = isWholeNumber(row.twdvBf) && Number(row.twdvBf) >= 0;

  row.noOfYearsCf = yearsBfValid
    ? row.deferred && getCaType(getSettings(caSettings), row.caType) !== "S19D"
      ? Number(row.noOfYearsBf)
      : Math.max(0, Number(row.noOfYearsBf) - 1)
    : null;
  row.iaRate = getInitialAllowanceRate(row.caType, caSettings);

  if (!yearsBfValid || !twdvBfValid || !costValid) {
    row.ia = null;
    row.aa = null;
    row.twdvCf = null;
    return row;
  }

  const fullyClaimed =
    Number(row.noOfYearsBf) === 0 || Number(row.twdvBf) === 0;
  const allowance = calculateRegisterAllowance(row, currentYa, caSettings);
  if (row.deferred || fullyClaimed) {
    row.ia = 0;
    row.aa = 0;
  } else {
    row.ia = allowance.ia;
    row.aa = allowance.aa;
  }

  if (getCaType(getSettings(caSettings), row.caType) === "S19D") {
    row.s19dDisposalAdjustment = allowance.s19dDisposalAdjustment;
    row.s19dWdaClaimedBf = allowance.s19dWdaClaimedBf;
    row.calculationWarning = allowance.calculationWarning;
  }
  const rawTwdvCf = Number(allowance.totalAmount) - Number(row.ia) - Number(row.aa);
  if (rawTwdvCf < 0) {
    row.calculationWarning =
      "Calculated TWDV c/f was below zero and has been capped at 0.";
  }
  row.twdvCf = Math.max(0, roundDollar(rawTwdvCf));
  return row;
}

export function validateAsset(
  input,
  currentYa = DEFAULT_CURRENT_YA,
  caSettings = getCaSettings()
) {
  const row = deriveAsset(input, currentYa, caSettings);
  const errors = {};

  if (!String(row.assetCategoryId || row.assetCategory || "").trim()) {
    errors.assetCategory = "Select an Asset Category.";
  }
  if (!isWholeNumber(row.costOfAsset) || Number(row.costOfAsset) <= 0) {
    errors.costOfAsset = "Enter a positive whole number.";
  }

  const yaText = String(row.yaOfPurchase ?? "");
  if (!/^\d{4}$/.test(yaText) || !isWholeNumber(row.yaOfPurchase)) {
    errors.yaOfPurchase = "Enter a valid 4-digit year.";
  } else if (Number(row.yaOfPurchase) > Number(currentYa)) {
    errors.yaOfPurchase = `YA of Purchase cannot be greater than Current YA (${currentYa}).`;
  } else if (
    isPLScheduleAsset(row) &&
    Number(row.yaOfPurchase) >= Number(currentYa)
  ) {
    errors.yaOfPurchase =
      "Asset expensed off must use a YA of purchase earlier than Current YA.";
  }

  if (!getFaRegisterCaTypes(caSettings).includes(row.caType)) {
    errors.caType = "Select a valid CA Type.";
  }
  if (
    isAssetExpensedOffCategory(row.assetCategory) &&
    EXTERNAL_TYPES.has(row.caType)
  ) {
    errors.caType =
      "Select an S19/S16 capital allowance type for assets expensed in P&L.";
  }
  if (typeof row.disposal !== "boolean") {
    errors.disposal = "Disposed in current year? must be True or False.";
  }

  if (isApplicableType(row.caType)) {
    if (!isWholeNumber(row.noOfYearsClaim) || Number(row.noOfYearsClaim) <= 0) {
      errors.noOfYearsClaim = "Enter a positive whole number.";
    }
    if (!isWholeNumber(row.noOfYearsBf) || Number(row.noOfYearsBf) < 0) {
      errors.noOfYearsBf = "Enter a non-negative whole number.";
    }
    if (typeof row.deferred !== "boolean") {
      errors.deferred = "Deferred in current year? must be True or False.";
    }
    if (!isWholeNumber(row.twdvBf) || Number(row.twdvBf) < 0) {
      errors.twdvBf = "Enter a non-negative whole dollar amount.";
    } else if (
      isWholeNumber(row.costOfAsset) &&
      Number(row.twdvBf) > Number(row.costOfAsset)
    ) {
      errors.twdvBf = "Base TWDV b/f cannot exceed Cost of Asset.";
    }
    if (
      isEnhancedTwdvType(row.caType, caSettings) &&
      (!isWholeNumber(row.enhancedTwdvBf) || Number(row.enhancedTwdvBf) < 0)
    ) {
      errors.enhancedTwdvBf = "Enter a non-negative whole number.";
    }
  }

  if (row.calculationWarning) errors.twdvCf = row.calculationWarning;
  return { row, errors, valid: Object.keys(errors).length === 0 };
}

export function displayValue(row, field) {
  if (row.disposal && !row.s19dDisposalAdjustment && ["ia", "aa", "twdvCf"].includes(field)) return "Disposed";
  if (field === "iaRate")
    return row.iaRate === null ? "N.A." : `${row.iaRate * 100}%`;
  if (row[field] === null && EXTERNAL_TYPES.has(row.caType)) return "N.A.";
  if (row[field] === null || row[field] === undefined || row[field] === "")
    return "-";
  if (field !== "yaOfPurchase" && typeof row[field] === "number") {
    return row[field];
  }
  return row[field];
}

function groupAndSum(rows, dimensions, amountField, fixedValues = {}) {
  const groups = new Map();

  rows.forEach((row) => {
    const values = Object.fromEntries(
      dimensions.map(([output, source]) => [output, row[source]])
    );
    const key = JSON.stringify(values);
    const existing = groups.get(key);
    if (existing) {
      existing[amountField] += Number(row.twdvBf);
    } else {
      groups.set(key, {
        ...values,
        ...fixedValues,
        [amountField]: Number(row.twdvBf),
      });
    }
  });

  return [...groups.values()];
}

export function getValidatedSummaryRows(
  rows,
  currentYa = DEFAULT_CURRENT_YA,
  caSettings = getCaSettings()
) {
  return rows
    .map((row) => validateAsset(row, currentYa, caSettings))
    .filter((result) => result.valid)
    .map((result) => result.row);
}

export function buildDataSummaries(
  sourceRows,
  currentYa = DEFAULT_CURRENT_YA,
  caSettings = getCaSettings()
) {
  const settings = getSettings(caSettings);
  const rows = getValidatedSummaryRows(sourceRows, currentYa, settings);
  const categoryGroups = new Map();

  rows
    .filter((row) => !isPLScheduleAsset(row))
    .forEach((row) => {
      const category = String(row.assetCategory).trim();
      const categoryId = row.assetCategoryId || category;
      const item = categoryGroups.get(categoryId) ?? {
        assetId: categoryId,
        categoryId,
        assetCategory: category,
        openingBalance: 0,
        additions: 0,
        disposal: 0,
        closingBalance: 0,
      };
      const cost = Number(row.costOfAsset);
      if (Number(row.yaOfPurchase) < Number(currentYa))
        item.openingBalance += cost;
      if (Number(row.yaOfPurchase) === Number(currentYa))
        item.additions += cost;
      if (row.disposal) item.disposal -= cost;
      item.closingBalance =
        item.openingBalance + item.additions + item.disposal;
      categoryGroups.set(categoryId, item);
    });

  const eligibleScheduleRows = rows.filter(
    (row) => !row.hp && !EXTERNAL_TYPES.has(row.caType)
  );
  const s19aRows = eligibleScheduleRows.filter(
    (row) => getCaType(settings, row.caType) === "S19A"
  );
  const s19bRows = eligibleScheduleRows.filter(
    (row) => getCaType(settings, row.caType) === "S19B"
  );
  const s19Rows = eligibleScheduleRows.filter(
    (row) => getCaType(settings, row.caType) === "S19"
  );
  const s19dRows = eligibleScheduleRows.filter(
    (row) => getCaType(settings, row.caType) === "S19D"
  );
  const s16Rows = eligibleScheduleRows.filter(
    (row) => getCaType(settings, row.caType) === "S16"
  );

  return {
    faSummary: [...categoryGroups.values()],
    faAdditions: rows
      .filter(
        (row) =>
          Number(row.yaOfPurchase) === Number(currentYa) &&
          !isPLScheduleAsset(row)
      )
      .map((row) => ({
        assetId: row.rowKey,
        categoryId: row.assetCategoryId || row.assetCategory,
        assetCategoryId: row.assetCategoryId || row.assetCategory,
        assetCategory: row.assetCategory,
        category: row.assetCategory,
        description: row.assetDescription,
        caName: row.caType,
        numberOfYears: row.noOfYearsClaim,
        cost: Number(row.costOfAsset),
        deferred: row.deferred,
      })),
    faDisposal: rows
      .filter((row) => row.disposal && !row.hp && !isPLScheduleAsset(row))
      .map((row) => ({
        assetId: row.rowKey,
        categoryId: row.assetCategoryId || row.assetCategory,
        assetCategoryId: row.assetCategoryId || row.assetCategory,
        assetCategory: row.assetCategory,
        category: row.assetCategory,
        descriptionOfAsset: row.assetDescription,
        caName: row.caType,
        yaOfPurchase: Number(row.yaOfPurchase),
        fullyClaimed: row.twdvBf === null ? false : Number(row.twdvBf) === 0,
        noOfYearsBf: row.noOfYearsBf ?? "N.A.",
        cost: Number(row.costOfAsset),
        twdvBf: row.twdvBf ?? "N.A.",
      })),
    caSchedules: {
      s19d: s19dRows.map((row) => ({
        assetId: row.rowKey,
        yearOfAssessment: Number(row.yaOfPurchase),
        caName: row.caType,
        deferred: row.deferred,
        noOfYearsClaim: row.noOfYearsClaim,
        noOfYearsBf: row.noOfYearsBf,
        cost: Number(row.costOfAsset),
        twdvBf: Number(row.twdvBf),
        aa: row.aa,
        twdvCf: row.twdvCf,
      })),
      s19a: groupAndSum(
        s19aRows,
        [
          ["yearOfAssessment", "yaOfPurchase"],
          ["caName", "caType"],
          ["deferred", "deferred"],
          ["noOfYearsClaim", "noOfYearsClaim"],
          ["noOfYearsBf", "noOfYearsBf"],
        ],
        "twdvBf"
      ),
      s19b: groupAndSum(
        s19bRows,
        [
          ["yearOfAssessment", "yaOfPurchase"],
          ["caName", "caType"],
          ["deferred", "deferred"],
          ["noOfYearsClaim", "noOfYearsClaim"],
          ["noOfYearsBf", "noOfYearsBf"],
        ],
        "baseTwdvBf",
        { enhancedTwdvBf: null }
      ),
      s19: groupAndSum(
        s19Rows,
        [
          ["yearOfAssessment", "yaOfPurchase"],
          ["caName", "caType"],
          ["deferred", "deferred"],
          ["noOfYearsClaim", "noOfYearsClaim"],
          ["noOfYearsBf", "noOfYearsBf"],
        ],
        "twdvBf"
      ),
      s16: groupAndSum(
        s16Rows,
        [
          ["yearOfAssessment", "yaOfPurchase"],
          ["caSummary", "caType"],
          ["deferred", "deferred"],
          ["noOfYearsClaim", "noOfYearsClaim"],
          ["noOfYearsBf", "noOfYearsBf"],
        ],
        "twdvBf",
        {
          caName: "S16 - (LIA / IBA)",
          disregard: false,
          disregardRate: null,
        }
      ),
    },
  };
}
